import React, { useEffect } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import i18n from 'i18n/i18n'
import { detectBrowserLanguage } from 'i18n/detectBrowserLanguage'
import { getPublicOverview } from 'services/transparency-service'
import { getPublicBranding } from 'services/company-service'
import { isVoTenant } from 'utils/tenant_utils'
import { applyPrimaryColorVars, clearPrimaryColorVars } from 'utils/color_utils'
import styled from 'styled-components'
import { Spinner } from 'Components/common/Spinner'
import { TransparencyHeader } from './components/TransparencyHeader'
import { FrameworkBadges } from './components/FrameworkBadges'
import { GovernanceOverview } from './components/GovernanceOverview'
import { TrendIndicator } from './components/TrendIndicator'
import { PublicPolicies } from './components/PublicPolicies'
import {
  TransparencyPageContainer,
  TransparencyPageContent,
  SectionTitle,
} from './styles'

const LoadingBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
`

const ErrorBox = styled.div`
  text-align: center;
  padding: 80px 0;
`

const ErrorTitle = styled.h3`
  font-size: 24px;
  margin: 0 0 16px 0;
  color: var(--vo-slate);
  font-weight: 400;
`

const ErrorMessage = styled.p`
  font-size: 14px;
  margin: 0;
  color: var(--vo-slate);
`

/**
 * PublicTransparencyPage
 * - No authentication required
 * - Accessed via /transparency/:companyKey (no token param)
 * - Shows high-level transparency information only
 * - noindex/nofollow enforced
 */
export const PublicTransparencyPage = ({ companyKey }) => {
  const { t } = useTranslation('transparency')

  useEffect(() => {
    i18n.changeLanguage(detectBrowserLanguage())
  }, [])

  const { isLoading, error, data } = useQuery(
    ['publicTransparency', companyKey],
    () => getPublicOverview(companyKey),
    { enabled: !!companyKey }
  )

  const organizationData = data?.data

  const { data: brandingData } = useQuery(
    ['publicBranding', companyKey],
    () => getPublicBranding(companyKey),
    { enabled: !!companyKey, retry: false, staleTime: 5 * 60 * 1000 }
  )

  // VO's own tenant always shows its real logo here, regardless of whether the 'vo'
  // company record itself has branding configured/plan-eligible — same exception as
  // SideBar and the Policy PDF cover page. Dark gradient header (see styles.js), so the
  // white logo variant, matching SideBar's open-menu logo on the same dark background.
  const brandingLogoUrl = isVoTenant(companyKey)
    ? '/img/logo-horizontal-white.png'
    : (brandingData?.data?.logoUrl ?? null)

  useEffect(() => {
    const b = brandingData?.data
    if (!b || b.default) return
    const root = document.documentElement
    if (b.primaryColor) applyPrimaryColorVars(root, b.primaryColor)
    if (b.secondaryColor) root.style.setProperty('--color-secondary', b.secondaryColor)
    if (b.faviconUrl) {
      const favicon = document.querySelector('link[rel="icon"]')
      if (favicon) favicon.href = b.faviconUrl
    }
    return () => {
      clearPrimaryColorVars(root)
      root.style.removeProperty('--color-secondary')
    }
  }, [brandingData])

  useEffect(() => {
    document.title = organizationData?.organization?.name || t('public.title')
    let metaRobots = document.querySelector('meta[name="robots"]')
    if (!metaRobots) {
      metaRobots = document.createElement('meta')
      metaRobots.name = 'robots'
      document.head.appendChild(metaRobots)
    }
    metaRobots.content = 'noindex, nofollow'
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow'
      }
    }
  }, [organizationData?.organization?.name, t])

  if (isLoading) {
    return (
      <TransparencyPageContainer>
        <LoadingBox>
          <Spinner />
        </LoadingBox>
      </TransparencyPageContainer>
    )
  }

  if (error || !organizationData) {
    return (
      <TransparencyPageContainer>
        <ErrorBox>
          <ErrorTitle>{t('public.unavailableTitle')}</ErrorTitle>
          <ErrorMessage>{t('public.unavailableMessage')}</ErrorMessage>
        </ErrorBox>
      </TransparencyPageContainer>
    )
  }

  const { organization, frameworkBadges, trendIndicator, governanceStructure, policies } = organizationData

  return (
    <TransparencyPageContainer>
      <TransparencyHeader
        name={organization?.name}
        description={organization?.description}
        lastUpdated={organization?.lastUpdated}
        brandingLogoUrl={brandingLogoUrl}
      />

      <TransparencyPageContent>
        {trendIndicator && (
          <TrendIndicator
            label={trendIndicator.label}
            description={trendIndicator.description}
            trend={trendIndicator.trend}
          />
        )}

        {frameworkBadges && frameworkBadges.length > 0 && (
          <div>
            <SectionTitle>{t('shared.commitments')}</SectionTitle>
            <FrameworkBadges badges={frameworkBadges} />
          </div>
        )}

        {governanceStructure && (
          <div style={{ marginBottom: 36 }}>
            <SectionTitle>{t('shared.governanceStructure')}</SectionTitle>
            <GovernanceOverview
              categories={governanceStructure.categories}
              overallPosture={governanceStructure.overallPosture}
            />
          </div>
        )}

        {policies && policies.length > 0 && (
          <div>
            <SectionTitle>{t('shared.publicPolicies')}</SectionTitle>
            <PublicPolicies policies={policies} />
          </div>
        )}
      </TransparencyPageContent>
    </TransparencyPageContainer>
  )
}
